import { useApolloClient, useMutation, useQuery, useReactiveVar } from '@apollo/client';

import {
  GET_CLIENTS,
  DELETE_CLIENT,
  RESTORE_CLIENT,
  UPDATE_CLIENT,
  GET_CLIENT,
  GET_CLIENT_OVERVIEWS
} from './client.queries';
import { IClientsResults } from './client.types';
import { useCallback, useMemo } from 'react';
import { IClient, Identifiable, IEntityMember } from '../../../lib/types';
import { updateClientCache } from './client.utils';
import { IArchivableProps, IUpdateCompanyPermissionsVariantParams } from '../../../lib/query.types';
import { updateEntityMemberCache } from '../entity-members/entity-members.utils';
import { activeCompanyLoadingVar } from '../companies/company.service';
import { IPermanent } from '../request-group/request-group.types';
import { GET_ENTITIES, GET_ENTITY } from '../entities/entity.queries';
import { GET_ENTITY_MEMBERS } from '../entity-members/entity-members.queries';
import { GET_REQUEST, LIST_REQUESTS_AS_STAFF } from '../request/request.queries';
import { GET_REQUEST_GROUP, GET_REQUEST_GROUPS } from '../request-group/request-group.queries';
import { GET_CLIENT_INVITE, GET_CLIENT_INVITES } from '../client-invite/client-invite.queries';

export const useClient = (variables: Identifiable) => {
  const { data, loading, error, called, refetch, networkStatus, observable, previousData } = useQuery<{ client: IClient }>(GET_CLIENT, { fetchPolicy: 'cache-and-network', variables });

  const clientData = useMemo(() => data?.client, [data?.client]);

  return useMemo(() => ({
    loading,
    error,
    called,
    refetch,
    networkStatus,
    observable,
    previousData,
    client: clientData
  }), [loading, error, called, refetch, networkStatus, observable, previousData, clientData]);
};

export const useClients = () => {
  const activeCompanyLoading = useReactiveVar(activeCompanyLoadingVar);
  const { data, loading, error, called, refetch, networkStatus, observable, previousData } = useQuery<IClientsResults, IArchivableProps>(GET_CLIENTS, {
    fetchPolicy: 'cache-and-network',
    variables: { includeArchived: true }
  });

  const clients = useMemo(() => data?.clients, [data?.clients]);
  const combinedLoading = useMemo(() => loading || activeCompanyLoading, [loading, activeCompanyLoading]);

  return useMemo(() => ({
    loading: combinedLoading,
    error,
    called,
    refetch,
    networkStatus,
    observable,
    previousData,
    clients
  }), [combinedLoading, error, called, refetch, networkStatus, observable, previousData, clients]);
};

export const useClientOverviews = () => {
  const activeCompanyLoading = useReactiveVar(activeCompanyLoadingVar);
  const { data, loading, error, called, refetch, networkStatus, observable, previousData } = useQuery<IClientsResults, IArchivableProps>(GET_CLIENT_OVERVIEWS, {
    fetchPolicy: 'cache-and-network',
    variables: { includeArchived: true }
  });

  const clients = useMemo(() => data?.clients, [data?.clients]);
  const combinedLoading = useMemo(() => loading || activeCompanyLoading, [loading, activeCompanyLoading]);

  return useMemo(() => ({
    loading: combinedLoading,
    error,
    called,
    refetch,
    networkStatus,
    observable,
    previousData,
    clients
  }), [combinedLoading, error, called, refetch, networkStatus, observable, previousData, clients]);
};

export const useDeleteClient = () => {
  const client = useApolloClient();

  const [mutation, rest] = useMutation<{ deleteClient: IClient }, Identifiable & IPermanent>(DELETE_CLIENT, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }, { variables }) => {
      if (data) updateClientCache({ cache, clients: [data.deleteClient], remove: variables?.permanent });

      if (variables?.permanent)
        client.refetchQueries({
          include: [
            GET_CLIENTS,
            GET_CLIENT_INVITE,
            GET_CLIENT_INVITES,
            GET_ENTITIES,
            GET_ENTITY,
            GET_ENTITY_MEMBERS,
            GET_REQUEST,
            LIST_REQUESTS_AS_STAFF,
            GET_REQUEST_GROUP,
            GET_REQUEST_GROUPS
          ]
        });
    }
  });

  const deleteClient = useCallback((variables: Identifiable & IPermanent) => mutation({ variables }), [mutation]);

  return { deleteClient, ...rest };
};

export const useRestoreClient = () => {
  const [mutation, rest] = useMutation<{ restoreClient: IClient }, Identifiable>(RESTORE_CLIENT, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data) updateClientCache({ cache, clients: [data.restoreClient] });
    }
  });

  const restoreClient = useCallback((variables: Identifiable) => mutation({ variables }), [mutation]);

  return { restoreClient, ...rest };
};

export const useUpdateClient = () => {
  const [mutation, rest] = useMutation<
    { updateClient: { client: IClient; members?: IEntityMember[] } },
    IUpdateCompanyPermissionsVariantParams
  >(UPDATE_CLIENT, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data) {
        // Update client cache
        updateClientCache({ cache, clients: [data.updateClient.client] });

        // Update entity members cache for current user (belonging to client), and for all entities that that client is a part of
        if (data.updateClient.members) {
          updateEntityMemberCache({
            cache,
            entityMembers: data.updateClient.members,
            variables: { userId: data.updateClient.client.user._id }
          });

          data.updateClient.members.map((member) =>
            updateEntityMemberCache({
              cache,
              entityMembers: [member],
              variables: { entityId: member.entity._id }
            })
          );
        }
      }
    }
  });

  const updateClient = useCallback(
    (variables: IUpdateCompanyPermissionsVariantParams) => mutation({ variables }),
    [mutation]
  );

  return { updateClient, ...rest };
};
